import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import { EntryType } from 'shared/models/sheet/Sheet';
import { routes } from 'shared/routes';
import { ITitleHeader } from 'shared/utils/hooks/ITitleHeader';
import useTitleUpdate from 'shared/utils/hooks/useTitleUpdate';
import { Permission } from 'store/components/auth/authModels';
import { setSheetGridStatus } from 'store/entities/appConfig/actions';
import { useQueryStatusRedirect } from 'modules/clients/content/TimeAndExpensePage/hooks';
import { loadClientAssignmentsWithLinked } from 'store/entities/configuration/configurationAction';
import EmployeeSheetDetailPage from 'modules/clients/content/TimeAndExpensePage/EmployeeSheetDetailPage';
import SheetsRejectedPage from 'modules/clients/content/TimeAndExpensePage/RejectedSheets/SheetsRejectedPage';
import SheetsRecalledPage from 'modules/clients/content/TimeAndExpensePage/SheetsInProgress/SheetsRecalledPage';
import SheetsWorkingPage from 'modules/clients/content/TimeAndExpensePage/SheetsInProgress/SheetsWorkingPage';
import SheetsSubmittedAllPage from 'modules/clients/content/TimeAndExpensePage/SheetsSubmitted/SheetsAllPage';
import SheetsSubmittedPage from 'modules/clients/content/TimeAndExpensePage/SheetsSubmitted/SheetsSubmittedPage';
import { useWithClientEffect } from 'modules/settings/common/hooks/useWithClientEffect';
import { EmployeeSheetGroupDetailSidebar } from 'modules/timeAndExpense/components/EmployeeSheetDetail/EmployeeSheetGroupDetailSidebar';
import { selectWorkingEntryTypeFilter } from 'modules/timeAndExpense/components/WorkingPage/store/selectors';
import { selectTimeAndExpenseActiveStatus } from 'modules/timeAndExpense/store/selectors';
import { getSimpleSheets } from 'store/entities/timesheet/actions/sheetState';
import { StatusNames } from 'store/entities/timesheet/models/Status';
import { getUsers } from 'store/entities/users/actions';

export default function TimeAndExpensePageContent() {
    const activeStatus = useSelector(selectTimeAndExpenseActiveStatus);
    const entryTypeFilter = useSelector(selectWorkingEntryTypeFilter);

    useWithClientEffect((dispatch, clientId) => {
        dispatch(getSimpleSheets.init());
        dispatch(loadClientAssignmentsWithLinked.init({ purpose: Permission.SubmitSheets }));
        dispatch(getUsers.init({ purpose: Permission.ApproveSheets, client_id: clientId }));
        dispatch(setSheetGridStatus(StatusNames.WORKING));
    }, []);

    const [title, setTitle] = useState<ITitleHeader>({
        header: 'Time & Expense Entry',
        headerMobile: 'Time & Expense',
    });

    useQueryStatusRedirect();

    useEffect(() => {
        if (entryTypeFilter === undefined || activeStatus === StatusNames.WORKING) {
            setTitle({
                header: 'Time & Expense Entry',
                headerMobile: 'Time & Expense',
            });
            return;
        }
        if (entryTypeFilter === EntryType.TIME) {
            setTitle({
                header: 'Time Entry',
                headerMobile: 'Time Entry',
            });
        } else if (entryTypeFilter === EntryType.EXPENSE) {
            setTitle({
                header: 'Expense Entry',
                headerMobile: 'Expense Entry',
            });
        }
    }, [entryTypeFilter, activeStatus, setTitle]);

    useTitleUpdate(title.header, title.headerMobile);

    const getPageByStatus = () => {
        switch (activeStatus) {
            case StatusNames.WORKING:
                return (
                    <SheetsWorkingPage />
                );
            case StatusNames.RECALLED:
                return (
                    <SheetsRecalledPage />
                );
            case StatusNames.REJECTED:
                return (
                    <SheetsRejectedPage />
                );
            case StatusNames.ALL:
                return (
                    <SheetsSubmittedAllPage />
                );
            default:
                return (
                    <SheetsSubmittedPage />
                );
        }
    };

    return (
        <>
            {getPageByStatus()}
            <EmployeeSheetGroupDetailSidebar />
            <Route
                path={routes.CLIENT.TIME_AND_EXPENSE.SHEET_DETAIL}
                component={EmployeeSheetDetailPage}
            />
        </>
    );
}
